import { Box, ButtonBase, Typography } from "@mui/material";
import { ChevronRight } from "@mui/icons-material";
import { useNavigate } from "react-router-dom";
import { Icon } from "@iconify/react";
import { useMutation, useQuery } from "react-query";
import { useSnackbar } from "notistack";
import { useTranslation } from "react-i18next";
import { useTelegram } from "../../../hooks";
import { createSbpPayment } from "../../../services/payments";
import { getUserInfo } from "../../../services/me";
import { useFeatureFlags } from "../../../contexts/FeatureFlagsContext";

export function WalletTopBar() {
  const navigate = useNavigate();
  const { tg, user } = useTelegram();
  const { enqueueSnackbar } = useSnackbar();
  const { guardFeature } = useFeatureFlags();
  const { t } = useTranslation("home");

  // Создание SBP-задачи после успешного сканирования QR — сразу редиректит
  // на страницу подтверждения оплаты.
  const { mutate: submitQr } = useMutation(createSbpPayment, {
    onSuccess: (payment) => {
      navigate(`/pay-qr/${payment.id}`);
    },
    onError: (err: unknown) => {
      const data = (err as { response?: { data?: { message?: string } } })?.response
        ?.data;
      const msg =
        typeof data?.message === "string"
          ? data.message
          : t("topbar.payment_error");
      enqueueSnackbar(msg, { variant: "error" });
    },
  });

  const handleQrClick = () => {
    // Проверяем feature-flag «sbp_qr» — админ мог выключить.
    if (!guardFeature("sbp_qr")) return;

    // Пробуем открыть встроенный Telegram-сканер: камера открывается прямо
    // поверх WebApp, без перехода на страницу /pay-qr.
    const anyTg = tg as unknown as {
      showScanQrPopup?: (
        params: { text?: string },
        cb: (data: string) => boolean | void,
      ) => void;
      closeScanQrPopup?: () => void;
    };
    if (typeof anyTg.showScanQrPopup === "function") {
      try {
        anyTg.showScanQrPopup({ text: t("topbar.qr_hint") }, (data) => {
          if (!data) return true;
          anyTg.closeScanQrPopup?.();
          submitQr({ qrPayload: data });
          return true;
        });
        return;
      } catch {
        /* fallback ниже */
      }
    }
    // Fallback: старый десктоп Telegram, где сканер не поддерживается —
    // ведём на страницу с ручным вводом / выбором картинки.
    navigate("/pay-qr");
  };

  const { data: meData } = useQuery({ queryFn: getUserInfo, queryKey: ["me"], refetchOnWindowFocus: false });

  const username = user?.username
    ? `@${user.username}`
    : meData?.first_name
      ? [meData.first_name, meData.last_name].filter(Boolean).join(" ")
      : [user?.first_name, user?.last_name].filter(Boolean).join(" ") ||
        t("topbar.profile");

  const avatarUrl = meData?.avatar_url || user?.photo_url;
  const initial =
    meData?.first_name?.[0]?.toUpperCase() ||
    user?.first_name?.[0]?.toUpperCase() ||
    user?.username?.[0]?.toUpperCase() ||
    "?";

  return (
    <Box
      sx={{
        display: "flex",
        alignItems: "center",
        justifyContent: "space-between",
        width: "100%",
        marginBottom: "6px",
      }}
    >
      <ButtonBase
        onClick={() => navigate("/personal-data")}
        sx={(theme) => ({
          display: "flex",
          alignItems: "center",
          gap: "10px",
          padding: "5px 12px 5px 5px",
          borderRadius: "999px",
          color: theme.palette.text.primary,
          // Тот же тон, что и у «Мои карты» / «Последние транзакции» —
          // secondary.main. В светлой теме — тонкий контур, в тёмной без.
          backgroundColor: theme.palette.secondary.main,
          border:
            theme.palette.mode === "light"
              ? "1px solid rgba(0, 0, 0, 0.06)"
              : "none",
          transition: "opacity 0.2s ease-in-out",
          "&:active": { transform: "scale(0.97)" },
        })}
      >
        <Box
          sx={(theme) => ({
            width: 30,
            height: 30,
            borderRadius: "999px",
            display: "flex",
            alignItems: "center",
            justifyContent: "center",
            fontSize: "13px",
            fontWeight: 700,
            color: theme.palette.primary.contrastText,
            backgroundColor: theme.palette.primary.main,
            backgroundImage: avatarUrl ? `url(${avatarUrl})` : "none",
            backgroundSize: "cover",
            backgroundPosition: "center",
            flexShrink: 0,
          })}
        >
          {avatarUrl ? "" : initial}
        </Box>
        <Typography
          sx={{
            fontSize: "14px",
            fontWeight: 500,
            maxWidth: "160px",
            overflow: "hidden",
            textOverflow: "ellipsis",
            whiteSpace: "nowrap",
            color: "inherit",
          }}
        >
          {username}
        </Typography>
        {/* Стрелка «в профиль» — обычный MUI-шеврон, как в списках профиля. */}
        <ChevronRight sx={{ fontSize: 18, color: "text.secondary", opacity: 0.7 }} />
      </ButtonBase>

      {/* QR-сканер: без фона, только иконка. При клике сразу открывается
          Telegram-сканер камеры; на десктопе (fallback) — /pay-qr со вводом
          вручную. */}
      <ButtonBase
        onClick={handleQrClick}
        aria-label={t("topbar.scan_qr_aria")}
        sx={(theme) => ({
          width: 40,
          height: 40,
          display: "flex",
          alignItems: "center",
          justifyContent: "center",
          color: theme.palette.text.primary,
          transition: "opacity 0.2s ease-in-out",
          "&:active": { transform: "scale(0.9)", opacity: 0.7 },
        })}
      >
        <Icon
          icon="solar:qr-code-outline"
          width={26}
          height={26}
          color="currentColor"
        />
      </ButtonBase>
    </Box>
  );
}
